import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import type { OfferView } from '../api/client';
import { useAuth } from '../lib/auth';
import { useMessagesLive } from '../lib/messages';
import { CallOfferCard } from './CallOffer';

/**
 * Call for you, wherever the person is in Stitch (1.13.0): a receptionist's ask shows at the corner
 * of every page while the caller holds, with the same buttons as the card in their direct messages
 * (not over that conversation itself, where the card is already). It goes a few seconds after the
 * ask is over, or when they close it.
 */
const LIVE = ['sent', 'seen', 'replied'];

export function CallOfferPopups({ domain }: { domain: string }) {
  const { state } = useAuth();
  const myId = state?.user?.id ?? '';
  const { pathname } = useLocation();
  const [items, setItems] = useState<OfferView[]>([]);
  const [closed, setClosed] = useState<Set<string>>(() => new Set());
  useMessagesLive(
    (e) => {
      const o = e.offer;
      if (e.type !== 'offer' || !o || o.to.id !== myId) return;
      if (e.tenant && e.tenant.toLowerCase() !== domain.toLowerCase()) return;
      setItems((cur) => (cur.some((x) => x.id === o.id) ? cur.map((x) => (x.id === o.id ? o : x)) : LIVE.includes(o.state) ? [...cur, o] : cur));
    },
    Boolean(myId)
  );
  // Over: gone after a few seconds (the card in the conversation keeps the record).
  useEffect(() => {
    const over = items.filter((o) => !LIVE.includes(o.state));
    if (!over.length) return;
    const t = window.setTimeout(() => setItems((cur) => cur.filter((o) => LIVE.includes(o.state))), 5000);
    return () => window.clearTimeout(t);
  }, [items]);
  const shown = items.filter((o) => !closed.has(o.id) && !(o.conversationId && pathname.includes(`/messages/${o.conversationId}`)));
  if (!shown.length) return null;
  const base = `/t/${encodeURIComponent(domain)}`;
  return (
    <div className="call-offer-popups" role="region" aria-label="Calls for you">
      {shown.map((o) => (
        <div key={o.id} className="call-offer-popup">
          <CallOfferCard domain={domain} offerId={o.id} myId={myId} initial={o} />
          <div className="call-offer-popup-foot">
            {o.conversationId && <Link to={`${base}/messages/${o.conversationId}`}>Open the conversation</Link>}
            <button type="button" className="link-btn" onClick={() => setClosed((c) => new Set(c).add(o.id))}>
              Close
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
